'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useUser } from '@/contexts/user'
import { useChangelogStore } from '@/stores/changelogStore'
import { useLocale, useTranslations } from 'next-intl'
import { getLocalizedPath } from '@/lib/i18n-utils'
import type { Locale } from '@/i18n/config'
import directusImageLoader from '@/lib/directus-image-loader'
import { DraftBadge } from '@/components/blog/draft-badge'
import { IS_PROD } from '@/lib/constant'
import { findChangelogTranslation } from '@/lib/changelog-translations'

// Define paths where the notification should NOT appear
const BLOCKED_PATHS = [
  '/login',
  '/project',
  '/remix',
  '/payment',
  '/settings',
  '/dashboard',
  '/domains',
  '/invitation',
  '/subscription',
  '/usage-history',
  '/new',
  '/legal',
  '/offer',
  '/payment-link-error',
  '/pricing',
  '/qa',
  '/template',
  '/use-case',
]

const LatestChangelogNotification = () => {
  const pathname = usePathname()
  const isBlocked = BLOCKED_PATHS.some((p) => pathname.startsWith(p))

  const { changelogs, loading, markAsRead } = useChangelogStore()
  const { user } = useUser()
  const [isOpen, setIsOpen] = useState(false)
  const [gateOk, setGateOk] = useState(true)
  const locale = useLocale()
  const t = useTranslations('changelog_notification')

  // Pick the latest changelog that explicitly enables popup and has a translation
  const latestChangelog = React.useMemo(() => {
    return (
      changelogs?.find((cl) => {
        const statusOk = IS_PROD
          ? cl.status === 'published'
          : cl.status === 'published' || cl.status === 'draft'
        const translation = findChangelogTranslation(cl, locale as Locale, {
          fallbackToAny: !IS_PROD,
        })
        return statusOk && cl.show_popup_notification === true && !!translation
      }) || null
    )
  }, [changelogs, locale])
  const acknowledgedRef = useRef(false)

  useEffect(() => {
    // Gate: don't show while onboarding survey is pending
    try {
      const done =
        (Array.isArray(user?.onboarding_answers) &&
          (user?.onboarding_answers?.length || 0) > 0) ||
        localStorage.getItem('onboarding_completed') === '1'
      setGateOk(!!done)
    } catch {}
  }, [user])

  useEffect(() => {
    if (
      !isBlocked &&
      gateOk &&
      !loading &&
      latestChangelog &&
      !latestChangelog.isRead &&
      latestChangelog.show_popup_notification === true
    ) {
      setIsOpen(true)
    }
  }, [loading, latestChangelog, gateOk, isBlocked])

  if (isBlocked || !gateOk || !isOpen || !latestChangelog) {
    return null
  }

  const acknowledgeOnce = () => {
    if (acknowledgedRef.current) return
    if (latestChangelog) {
      markAsRead(latestChangelog.id)
      acknowledgedRef.current = true
    }
  }

  const handleClose = () => {
    acknowledgeOnce()
    setIsOpen(false)
  }

  const handleLearnMore = () => {
    acknowledgeOnce()
    const slug = latestChangelog.slug || ''
    const path = getLocalizedPath(locale as Locale, `/changelog/${slug.trim()}`)
    window.open(path, '_blank')
    setIsOpen(false)
  }

  const imageId =
    latestChangelog.cover_image &&
    typeof latestChangelog.cover_image === 'object'
      ? latestChangelog.cover_image.id
      : latestChangelog.cover_image
  const imageUrl = imageId || '/og-image/placeholder.png'

  const translation =
    latestChangelog &&
    findChangelogTranslation(latestChangelog, locale as Locale, {
      fallbackToAny: !IS_PROD,
    })

  if (!translation && !latestChangelog.title) return null

  const title = translation?.title || latestChangelog.title
  const headline = translation?.headline || ''

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) acknowledgeOnce()
        setIsOpen(open)
      }}
    >
      <DialogContent
        overlayClassName="backdrop-blur-sm"
        className="text-white w-full max-w-none sm:max-w-xl rounded-2xl border border-white/10 bg-gradient-to-br from-[#2a2a2a] to-[#121212] shadow-2xl"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold mb-2 flex items-center gap-2">
            {title}
            <DraftBadge draft={latestChangelog.status !== 'published'} />
          </DialogTitle>
          <div className="relative w-full aspect-video rounded-xl overflow-hidden my-4 bg-white/10 border border-white/10">
            <Image
              loader={directusImageLoader}
              src={imageUrl}
              alt={title || 'Feature image'}
              fill
              className="object-cover"
            />
          </div>
          {headline && (
            <DialogDescription className="text-gray-300">
              {headline}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="mt-4 flex justify-end gap-3">
          <Button
            type="button"
            variant="transparent"
            className="h-12 rounded-xl px-6 bg-white/10 text-white hover:bg-white/20 border border-white/10 shadow-sm"
            onClick={handleLearnMore}
          >
            {t('learn_more')}
          </Button>
          <Button
            type="button"
            variant="light"
            className="h-12 rounded-xl px-6 text-gray-900"
            onClick={handleClose}
          >
            {t('got_it')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LatestChangelogNotification
